/**
 * Unified Stabilization Hook
 * Provides multiple anti-handshake algorithms for document scanning
 *
 * Methods:
 * - off: Manual capture only
 * - deviceMotion: Gyroscope/Accelerometer-based (Option A)
 * - opticalFlow: Frame-to-frame motion analysis (Option B)
 * - burstSelect: Burst capture + best frame selection (Option C)
 * - stabilityTimer: Real-time sharpness tracking (Option D)
 * - edgeMotion: Document edge detection + motion (Option E - Google-style)
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// Types
export type StabilizationMethod =
  | 'off'
  | 'deviceMotion'
  | 'opticalFlow'
  | 'burstSelect'
  | 'stabilityTimer'
  | 'edgeMotion';

export interface Point {
  x: number;
  y: number;
}

export interface StabilizationConfig {
  method: StabilizationMethod;
  autoCapture: boolean;
  showIndicator: boolean;
  hapticFeedback: boolean;
  // Thresholds
  motionThreshold: number;      // m/s² for device motion
  stabilityDuration: number;    // ms to wait before capture
  burstFrameCount: number;      // frames for burst mode
  opticalFlowThreshold: number; // % pixel change
  edgeStabilityThreshold: number; // px corner movement
}

export interface StabilizationState {
  method: StabilizationMethod;
  isStable: boolean;
  stabilityScore: number;       // 0-100
  isAnalyzing: boolean;
  statusMessage: string;
  // Method-specific data
  detectedCorners: Point[] | null;
  motionMagnitude: number;
  frameMotion: number;
  sharpnessVariance: number;
}

export interface StabilizationResult {
  state: StabilizationState;
  config: StabilizationConfig;
  setMethod: (method: StabilizationMethod) => void;
  setConfig: (config: Partial<StabilizationConfig>) => void;
  startAnalysis: (videoElement: HTMLVideoElement, canvas: HTMLCanvasElement) => void;
  stopAnalysis: () => void;
  captureWithMethod: () => Promise<{ imageData: ImageData; dataUrl: string } | null>;
  reset: () => void;
  isSupported: (method: StabilizationMethod) => boolean;
}

// Default configuration
const DEFAULT_CONFIG: StabilizationConfig = {
  method: 'stabilityTimer',
  autoCapture: true,
  showIndicator: true,
  hapticFeedback: true,
  motionThreshold: 0.3,
  stabilityDuration: 500,
  burstFrameCount: 15,
  opticalFlowThreshold: 5,
  edgeStabilityThreshold: 5,
};

// Method descriptions for UI
export const METHOD_INFO: Record<StabilizationMethod, { label: string; description: string }> = {
  off: {
    label: 'Off (Manual)',
    description: 'Manual capture only, no stabilization',
  },
  deviceMotion: {
    label: 'Device Motion Sensors',
    description: 'Uses gyroscope & accelerometer to detect device stability',
  },
  opticalFlow: {
    label: 'Optical Flow Analysis',
    description: 'Compares consecutive frames to detect image motion',
  },
  burstSelect: {
    label: 'Burst + Best Frame',
    description: 'Captures multiple frames and selects the sharpest one',
  },
  stabilityTimer: {
    label: 'Stability Timer',
    description: 'Tracks image sharpness over time, captures when stable',
  },
  edgeMotion: {
    label: 'Edge + Motion (Google-style)',
    description: 'Detects document edges and waits for stable positioning',
  },
};

export function useStabilization(
  initialConfig: Partial<StabilizationConfig> = {}
): StabilizationResult {
  const [config, setConfigState] = useState<StabilizationConfig>({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  });

  const [state, setState] = useState<StabilizationState>({
    method: config.method,
    isStable: false,
    stabilityScore: 0,
    isAnalyzing: false,
    statusMessage: '',
    detectedCorners: null,
    motionMagnitude: 0,
    frameMotion: 0,
    sharpnessVariance: 0,
  });

  // Refs for analysis
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analysisFrameRef = useRef<number | null>(null);
  const previousFrameRef = useRef<ImageData | null>(null);
  const sharpnessHistoryRef = useRef<number[]>([]);
  const cornerHistoryRef = useRef<Point[][] | null>(null);
  const stableStartTimeRef = useRef<number | null>(null);
  const deviceMotionRef = useRef<{ ax: number; ay: number; az: number }>({ ax: 0, ay: 0, az: 0 });

  // Check if method is supported
  const isSupported = useCallback((method: StabilizationMethod): boolean => {
    switch (method) {
      case 'off':
      case 'burstSelect':
      case 'stabilityTimer':
      case 'opticalFlow':
      case 'edgeMotion':
        return true;
      case 'deviceMotion':
        return 'DeviceMotionEvent' in window || 'DeviceOrientationEvent' in window;
      default:
        return false;
    }
  }, []);

  // Set method
  const setMethod = useCallback((method: StabilizationMethod) => {
    setConfigState(prev => ({ ...prev, method }));
    setState(prev => ({
      ...prev,
      method,
      isStable: false,
      stabilityScore: 0,
      statusMessage: '',
    }));
    // Reset analysis state
    sharpnessHistoryRef.current = [];
    cornerHistoryRef.current = null;
    stableStartTimeRef.current = null;
    previousFrameRef.current = null;
  }, []);

  // Set config
  const setConfig = useCallback((newConfig: Partial<StabilizationConfig>) => {
    setConfigState(prev => ({ ...prev, ...newConfig }));
    if (newConfig.method) {
      setState(prev => ({ ...prev, method: newConfig.method! }));
    }
  }, []);

  // Calculate sharpness using Laplacian variance
  const calculateSharpness = useCallback((imageData: ImageData): number => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const downsample = 4;

    let sum = 0;
    let sumSq = 0;
    let count = 0;

    for (let y = 1; y < height - 1; y += downsample) {
      for (let x = 1; x < width - 1; x += downsample) {
        const idx = (y * width + x) * 4;
        const gray = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;

        const topIdx = ((y - 1) * width + x) * 4;
        const bottomIdx = ((y + 1) * width + x) * 4;
        const leftIdx = (y * width + (x - 1)) * 4;
        const rightIdx = (y * width + (x + 1)) * 4;

        const grayTop = data[topIdx] * 0.299 + data[topIdx + 1] * 0.587 + data[topIdx + 2] * 0.114;
        const grayBottom = data[bottomIdx] * 0.299 + data[bottomIdx + 1] * 0.587 + data[bottomIdx + 2] * 0.114;
        const grayLeft = data[leftIdx] * 0.299 + data[leftIdx + 1] * 0.587 + data[leftIdx + 2] * 0.114;
        const grayRight = data[rightIdx] * 0.299 + data[rightIdx + 1] * 0.587 + data[rightIdx + 2] * 0.114;

        const laplacian = -4 * gray + grayTop + grayBottom + grayLeft + grayRight;
        sum += laplacian;
        sumSq += laplacian * laplacian;
        count++;
      }
    }

    if (count === 0) return 0;
    const mean = sum / count;
    return (sumSq / count) - (mean * mean);
  }, []);

  // Calculate Tenengrad (gradient magnitude) for better focus detection
  const calculateTenengrad = useCallback((imageData: ImageData): number => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const downsample = 4;

    let sumGrad = 0;
    let count = 0;

    for (let y = 1; y < height - 1; y += downsample) {
      for (let x = 1; x < width - 1; x += downsample) {
        const idx = (y * width + x) * 4;
        // Center pixel grayscale (idx used for neighbor calculations)
        void idx; // Mark as intentionally used for context

        const leftIdx = (y * width + (x - 1)) * 4;
        const rightIdx = (y * width + (x + 1)) * 4;
        const topIdx = ((y - 1) * width + x) * 4;
        const bottomIdx = ((y + 1) * width + x) * 4;

        const grayLeft = data[leftIdx] * 0.299 + data[leftIdx + 1] * 0.587 + data[leftIdx + 2] * 0.114;
        const grayRight = data[rightIdx] * 0.299 + data[rightIdx + 1] * 0.587 + data[rightIdx + 2] * 0.114;
        const grayTop = data[topIdx] * 0.299 + data[topIdx + 1] * 0.587 + data[topIdx + 2] * 0.114;
        const grayBottom = data[bottomIdx] * 0.299 + data[bottomIdx + 1] * 0.587 + data[bottomIdx + 2] * 0.114;

        // Sobel gradients
        const gx = grayRight - grayLeft;
        const gy = grayBottom - grayTop;
        sumGrad += gx * gx + gy * gy;
        count++;
      }
    }

    return count > 0 ? sumGrad / count : 0;
  }, []);

  // Calculate frame difference (optical flow simplified)
  const calculateFrameDiff = useCallback((current: ImageData, previous: ImageData): number => {
    if (current.width !== previous.width || current.height !== previous.height) {
      return 100;
    }

    const width = current.width;
    const height = current.height;
    const downsample = 8;
    let diff = 0;
    let count = 0;

    for (let y = 0; y < height; y += downsample) {
      for (let x = 0; x < width; x += downsample) {
        const idx = (y * width + x) * 4;
        const currGray = current.data[idx] * 0.299 + current.data[idx + 1] * 0.587 + current.data[idx + 2] * 0.114;
        const prevGray = previous.data[idx] * 0.299 + previous.data[idx + 1] * 0.587 + previous.data[idx + 2] * 0.114;
        diff += Math.abs(currGray - prevGray);
        count++;
      }
    }

    // Normalize to 0-100 (percentage of max possible diff)
    return count > 0 ? (diff / count / 255) * 100 : 100;
  }, []);

  // Detect document edges using Canny-like edge detection
  const detectDocumentEdges = useCallback((imageData: ImageData): Point[] | null => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    // Step 1: Convert to grayscale and apply Gaussian blur (simplified)
    const gray = new Float32Array(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        gray[y * width + x] = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
      }
    }

    // Step 2: Compute gradients (Sobel)
    const gradMag = new Float32Array(width * height);
    let maxGrad = 0;

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const gx = gray[(y - 1) * width + (x + 1)] - gray[(y - 1) * width + (x - 1)] +
                   2 * (gray[y * width + (x + 1)] - gray[y * width + (x - 1)]) +
                   gray[(y + 1) * width + (x + 1)] - gray[(y + 1) * width + (x - 1)];
        const gy = gray[(y + 1) * width + (x - 1)] - gray[(y - 1) * width + (x - 1)] +
                   2 * (gray[(y + 1) * width + x] - gray[(y - 1) * width + x]) +
                   gray[(y + 1) * width + (x + 1)] - gray[(y - 1) * width + (x + 1)];
        const mag = Math.sqrt(gx * gx + gy * gy);
        gradMag[y * width + x] = mag;
        if (mag > maxGrad) maxGrad = mag;
      }
    }

    // Step 3: Threshold to get edge pixels
    const threshold = maxGrad * 0.2;
    const edges: Point[] = [];
    for (let y = 10; y < height - 10; y++) {
      for (let x = 10; x < width - 10; x++) {
        if (gradMag[y * width + x] > threshold) {
          edges.push({ x, y });
        }
      }
    }

    // Step 4: Find corners using convex hull approximation
    // Simplified: find extreme points in each quadrant
    if (edges.length < 100) return null;

    const centerX = width / 2;
    const centerY = height / 2;

    // Find points closest to each corner region
    let topLeft: Point = { x: 0, y: 0 };
    let topRight: Point = { x: width, y: 0 };
    let bottomLeft: Point = { x: 0, y: height };
    let bottomRight: Point = { x: width, y: height };

    let minDistTL = Infinity;
    let minDistTR = Infinity;
    let minDistBL = Infinity;
    let minDistBR = Infinity;

    for (const p of edges) {
      // Top-left quadrant
      if (p.x < centerX && p.y < centerY) {
        const dist = p.x + p.y;
        if (dist < minDistTL) {
          minDistTL = dist;
          topLeft = p;
        }
      }
      // Top-right quadrant
      if (p.x >= centerX && p.y < centerY) {
        const dist = (width - p.x) + p.y;
        if (dist < minDistTR) {
          minDistTR = dist;
          topRight = p;
        }
      }
      // Bottom-left quadrant
      if (p.x < centerX && p.y >= centerY) {
        const dist = p.x + (height - p.y);
        if (dist < minDistBL) {
          minDistBL = dist;
          bottomLeft = p;
        }
      }
      // Bottom-right quadrant
      if (p.x >= centerX && p.y >= centerY) {
        const dist = (width - p.x) + (height - p.y);
        if (dist < minDistBR) {
          minDistBR = dist;
          bottomRight = p;
        }
      }
    }

    // Validate: corners should form a reasonable quadrilateral
    const minArea = width * height * 0.1; // At least 10% of frame
    const area = 0.5 * Math.abs(
      (topRight.x - topLeft.x) * (bottomLeft.y - topLeft.y) -
      (bottomLeft.x - topLeft.x) * (topRight.y - topLeft.y) +
      (bottomRight.x - topRight.x) * (bottomLeft.y - topRight.y) -
      (bottomLeft.x - topRight.x) * (bottomRight.y - topRight.y)
    );

    if (area < minArea) return null;

    return [topLeft, topRight, bottomRight, bottomLeft];
  }, []);

  // Calculate corner stability
  const calculateCornerStability = useCallback((currentCorners: Point[]): number => {
    if (!cornerHistoryRef.current || cornerHistoryRef.current.length === 0) {
      cornerHistoryRef.current = [currentCorners];
      return 0;
    }

    const history = cornerHistoryRef.current;
    const lastCorners = history[history.length - 1];

    // Calculate max corner movement
    let maxMovement = 0;
    for (let i = 0; i < 4; i++) {
      const dx = currentCorners[i].x - lastCorners[i].x;
      const dy = currentCorners[i].y - lastCorners[i].y;
      const movement = Math.sqrt(dx * dx + dy * dy);
      if (movement > maxMovement) maxMovement = movement;
    }

    // Update history (keep last 10 frames)
    history.push(currentCorners);
    if (history.length > 10) history.shift();

    // Return stability as inverse of movement (0-100)
    const stability = Math.max(0, 100 - maxMovement * 10);
    return stability;
  }, []);

  // Capture single frame
  const captureSingleFrame = useCallback((): { imageData: ImageData; dataUrl: string } | null => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);

    return { imageData, dataUrl };
  }, []);

  // Burst capture with best frame selection
  const burstCapture = useCallback(async (): Promise<{ imageData: ImageData; dataUrl: string } | null> => {
    const frames: { imageData: ImageData; dataUrl: string; score: number }[] = [];
    const frameCount = config.burstFrameCount;
    const interval = 33; // ~30fps

    setState(prev => ({ ...prev, isAnalyzing: true, statusMessage: 'Capturing frames...' }));

    for (let i = 0; i < frameCount; i++) {
      const frame = captureSingleFrame();
      if (frame) {
        const laplacian = calculateSharpness(frame.imageData);
        const tenengrad = calculateTenengrad(frame.imageData);
        const score = laplacian * 0.6 + tenengrad * 0.4;
        frames.push({ ...frame, score });
      }
      if (i < frameCount - 1) {
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    setState(prev => ({ ...prev, statusMessage: 'Selecting best frame...' }));

    // Find best frame
    let best = frames[0];
    for (const frame of frames) {
      if (frame.score > best.score) {
        best = frame;
      }
    }

    setState(prev => ({ ...prev, isAnalyzing: false, statusMessage: '' }));

    if (config.hapticFeedback && navigator.vibrate) {
      navigator.vibrate(50);
    }

    return best ? { imageData: best.imageData, dataUrl: best.dataUrl } : null;
  }, [config.burstFrameCount, config.hapticFeedback, captureSingleFrame, calculateSharpness, calculateTenengrad]);

  // Analysis loop
  const runAnalysis = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Capture frame for analysis (downsampled for performance)
    const analysisWidth = 320;
    const analysisHeight = 240;
    canvas.width = analysisWidth;
    canvas.height = analysisHeight;
    ctx.drawImage(video, 0, 0, analysisWidth, analysisHeight);
    const currentFrame = ctx.getImageData(0, 0, analysisWidth, analysisHeight);

    let isStable = false;
    let stabilityScore = 0;
    let statusMessage = '';
    let detectedCorners: Point[] | null = null;
    let frameMotion = 0;
    let sharpnessVariance = 0;

    switch (config.method) {
      case 'off':
        isStable = true;
        stabilityScore = 100;
        break;

      case 'deviceMotion': {
        const { ax, ay, az } = deviceMotionRef.current;
        const magnitude = Math.sqrt(ax * ax + ay * ay + az * az);
        // Subtract gravity (~9.8) and check threshold
        const motion = Math.abs(magnitude - 9.8);
        isStable = motion < config.motionThreshold;
        stabilityScore = Math.max(0, 100 - motion * 100);
        statusMessage = isStable ? 'Device stable' : 'Hold steady...';
        break;
      }

      case 'opticalFlow': {
        if (previousFrameRef.current) {
          frameMotion = calculateFrameDiff(currentFrame, previousFrameRef.current);
          isStable = frameMotion < config.opticalFlowThreshold;
          stabilityScore = Math.max(0, 100 - frameMotion * 2);
          statusMessage = isStable ? 'Image stable' : `Motion: ${frameMotion.toFixed(1)}%`;
        }
        previousFrameRef.current = currentFrame;
        break;
      }

      case 'stabilityTimer': {
        const sharpness = calculateSharpness(currentFrame);
        sharpnessHistoryRef.current.push(sharpness);
        if (sharpnessHistoryRef.current.length > 10) {
          sharpnessHistoryRef.current.shift();
        }

        // Calculate variance of sharpness
        if (sharpnessHistoryRef.current.length >= 5) {
          const mean = sharpnessHistoryRef.current.reduce((a, b) => a + b, 0) / sharpnessHistoryRef.current.length;
          sharpnessVariance = sharpnessHistoryRef.current.reduce((acc, val) => acc + (val - mean) ** 2, 0) / sharpnessHistoryRef.current.length;
          const normalizedVariance = Math.min(1, sharpnessVariance / 10000);
          isStable = normalizedVariance < 0.1;
          stabilityScore = Math.max(0, (1 - normalizedVariance) * 100);
          statusMessage = isStable ? 'Ready to capture' : `Stabilizing... ${stabilityScore.toFixed(0)}%`;
        }
        break;
      }

      case 'edgeMotion': {
        detectedCorners = detectDocumentEdges(currentFrame);

        if (detectedCorners) {
          const cornerStability = calculateCornerStability(detectedCorners);
          // Also check frame motion
          if (previousFrameRef.current) {
            frameMotion = calculateFrameDiff(currentFrame, previousFrameRef.current);
          }
          previousFrameRef.current = currentFrame;

          const motionStable = frameMotion < config.opticalFlowThreshold;
          const cornersStable = cornerStability > 80;

          isStable = motionStable && cornersStable;
          stabilityScore = (cornerStability + (100 - frameMotion * 2)) / 2;
          statusMessage = isStable ? 'Document detected - Ready!' :
                          !cornersStable ? 'Hold document steady...' :
                          'Detecting edges...';
        } else {
          statusMessage = 'Position document in frame';
          stabilityScore = 0;
        }
        break;
      }
    }

    // Update stable timer
    if (isStable) {
      if (!stableStartTimeRef.current) {
        stableStartTimeRef.current = Date.now();
      }
      const stableTime = Date.now() - stableStartTimeRef.current;
      if (stableTime >= config.stabilityDuration) {
        statusMessage = 'Auto-capturing...';
      }
    } else {
      stableStartTimeRef.current = null;
    }

    setState(prev => ({
      ...prev,
      isStable,
      stabilityScore,
      statusMessage,
      detectedCorners,
      frameMotion,
      sharpnessVariance,
    }));

    analysisFrameRef.current = requestAnimationFrame(runAnalysis);
  }, [config, calculateSharpness, calculateFrameDiff, detectDocumentEdges, calculateCornerStability]);

  // Device motion handler
  useEffect(() => {
    if (config.method !== 'deviceMotion') return;

    const handleMotion = (event: DeviceMotionEvent) => {
      const acc = event.accelerationIncludingGravity;
      if (acc) {
        deviceMotionRef.current = {
          ax: acc.x || 0,
          ay: acc.y || 0,
          az: acc.z || 0,
        };
      }
    };

    // Request permission on iOS
    if (typeof (DeviceMotionEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function') {
      (DeviceMotionEvent as unknown as { requestPermission: () => Promise<string> }).requestPermission()
        .then(response => {
          if (response === 'granted') {
            window.addEventListener('devicemotion', handleMotion);
          }
        })
        .catch(console.error);
    } else {
      window.addEventListener('devicemotion', handleMotion);
    }

    return () => {
      window.removeEventListener('devicemotion', handleMotion);
    };
  }, [config.method]);

  // Start analysis
  const startAnalysis = useCallback((videoElement: HTMLVideoElement, canvas: HTMLCanvasElement) => {
    videoRef.current = videoElement;
    canvasRef.current = canvas;

    // Reset state
    sharpnessHistoryRef.current = [];
    cornerHistoryRef.current = null;
    stableStartTimeRef.current = null;
    previousFrameRef.current = null;

    setState(prev => ({ ...prev, isAnalyzing: true }));

    if (config.method !== 'off' && config.method !== 'burstSelect') {
      analysisFrameRef.current = requestAnimationFrame(runAnalysis);
    }
  }, [config.method, runAnalysis]);

  // Stop analysis
  const stopAnalysis = useCallback(() => {
    if (analysisFrameRef.current) {
      cancelAnimationFrame(analysisFrameRef.current);
      analysisFrameRef.current = null;
    }
    setState(prev => ({ ...prev, isAnalyzing: false }));
  }, []);

  // Capture with current method
  const captureWithMethod = useCallback(async (): Promise<{ imageData: ImageData; dataUrl: string } | null> => {
    switch (config.method) {
      case 'burstSelect':
        return burstCapture();
      default:
        const result = captureSingleFrame();
        if (result && config.hapticFeedback && navigator.vibrate) {
          navigator.vibrate(50);
        }
        return result;
    }
  }, [config.method, config.hapticFeedback, burstCapture, captureSingleFrame]);

  // Reset
  const reset = useCallback(() => {
    stopAnalysis();
    setState({
      method: config.method,
      isStable: false,
      stabilityScore: 0,
      isAnalyzing: false,
      statusMessage: '',
      detectedCorners: null,
      motionMagnitude: 0,
      frameMotion: 0,
      sharpnessVariance: 0,
    });
    sharpnessHistoryRef.current = [];
    cornerHistoryRef.current = null;
    stableStartTimeRef.current = null;
    previousFrameRef.current = null;
  }, [config.method, stopAnalysis]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (analysisFrameRef.current) {
        cancelAnimationFrame(analysisFrameRef.current);
      }
    };
  }, []);

  return {
    state,
    config,
    setMethod,
    setConfig,
    startAnalysis,
    stopAnalysis,
    captureWithMethod,
    reset,
    isSupported,
  };
}

export default useStabilization;
